import axios from 'axios';
import { Capacitor } from '@capacitor/core';

const STORAGE_KEY = 'school_config';

/**
 * Memeriksa apakah aplikasi berjalan di platform native (Android/iOS).
 */
export const isNative = Capacitor.isNativePlatform();

/**
 * Mendapatkan konfigurasi sekolah aktif dari storage.
 * Khusus platform native. Jika di website, mengembalikan null agar memakai konfigurasi .env.
 * @returns {Object|null}
 */
export function getSchoolConfig() {
  if (!Capacitor.isNativePlatform()) {
    return null;
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (err) {
    console.error('Failed to parse school_config:', err);
    return null;
  }
}

/**
 * Menyimpan konfigurasi sekolah aktif ke storage.
 * @param {Object} config - { school_id, name, api_url, logo, pusher_key, pusher_cluster }
 */
export function setSchoolConfig(config) {
  if (!config || !config.api_url) {
    throw new Error('Konfigurasi sekolah tidak valid (api_url wajib ada).');
  }

  // Normalisasi URL (hilangkan trailing slash jika ada)
  let normalizedApiUrl = config.api_url.trim().replace(/\/+$/, '');
  if (!normalizedApiUrl.endsWith('/api')) {
    normalizedApiUrl += '/api';
  }

  const payload = {
    school_id: config.school_id || config.id || 'custom',
    school_slug: config.school_slug || config.school_id || process.env.VUE_APP_SCHOOL_NAME_SLUG || '',
    name: config.name || config.school_name || 'Sekolah Terhubung',
    api_url: normalizedApiUrl,
    logo: config.logo || null,
    pusher_key: config.pusher_key || process.env.VUE_APP_PUSHER_KEY || '',
    pusher_cluster: config.pusher_cluster || process.env.VUE_APP_PUSHER_CLUSTER || 'ap1',
    paired_at: new Date().toISOString()
  };

  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  return payload;
}

/**
 * Memperbarui bagian tertentu dari konfigurasi sekolah aktif di storage.
 * @param {Object} partialConfig - Data yang ingin diperbarui, misalnya { logo, name }
 * @returns {Object|null}
 */
export function updateSchoolConfig(partialConfig) {
  if (!Capacitor.isNativePlatform()) {
    return null;
  }
  try {
    const current = getSchoolConfig() || {};
    const updated = {
      ...current,
      ...partialConfig,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    return updated;
  } catch (err) {
    console.error('Failed to update school_config:', err);
    return null;
  }
}

/**
 * Mendapatkan logo sekolah aktif dari storage.
 * @returns {string|null}
 */
export function getSchoolLogo() {
  const config = getSchoolConfig();
  return config?.logo || null;
}

/**
 * Mendapatkan school_slug aktif dari storage atau fallback ke .env.
 * @returns {string}
 */
export function getSchoolSlug() {
  if (!Capacitor.isNativePlatform()) {
    return process.env.VUE_APP_SCHOOL_NAME_SLUG || process.env.VUE_APP_SCHOOL_SLUG || '';
  }

  const config = getSchoolConfig();
  return config?.school_slug || config?.school_id || process.env.VUE_APP_SCHOOL_NAME_SLUG || '';
}

/**
 * Menghapus konfigurasi sekolah aktif dan sesi terkait.
 */
export function clearSchoolConfig() {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem('token');
  localStorage.removeItem('user');
}

/**
 * Memeriksa apakah aplikasi sudah terhubung ke suatu sekolah.
 * Untuk website (non-native), selalu bernilai true karena build website sudah terikat ke .env.
 * @returns {boolean}
 */
export function hasSchoolConfig() {
  if (!Capacitor.isNativePlatform()) {
    return true;
  }
  const config = getSchoolConfig();
  return Boolean(config && config.api_url);
}

/**
 * Mendapatkan base API URL aktif.
 * Jika di website: SELALU menggunakan VUE_APP_API_URL dari .env.
 * Jika di mobile native: Menggunakan konfigurasi sekolah yang di-scan, fallback ke .env.
 * @returns {string}
 */
export function getSchoolApiUrl() {
  if (!Capacitor.isNativePlatform()) {
    return process.env.VUE_APP_API_URL || 'http://localhost:8000/api';
  }

  const config = getSchoolConfig();
  if (config && config.api_url) {
    return config.api_url;
  }
  return process.env.VUE_APP_API_URL || 'http://localhost:8000/api';
}

/**
 * Mendapatkan konfigurasi Pusher aktif.
 * Jika di website: SELALU menggunakan kredensial Pusher dari .env.
 * Jika di mobile native: Menggunakan kredensial dari sekolah yang di-scan, fallback ke .env.
 */
export function getSchoolPusherConfig() {
  if (!Capacitor.isNativePlatform()) {
    return {
      key: process.env.VUE_APP_PUSHER_KEY,
      cluster: process.env.VUE_APP_PUSHER_CLUSTER || 'ap1'
    };
  }

  const config = getSchoolConfig();
  return {
    key: config?.pusher_key || process.env.VUE_APP_PUSHER_KEY,
    cluster: config?.pusher_cluster || process.env.VUE_APP_PUSHER_CLUSTER || 'ap1'
  };
}

/**
 * Memvalidasi apakah URL / server sekolah aktif dan merespon API.
 * Mencoba memanggil endpoint /config atau health check.
 * @param {string} apiUrl
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export async function validateSchoolApi(apiUrl) {
  try {
    let normalized = apiUrl.trim().replace(/\/+$/, '');
    if (!normalized.endsWith('/api')) {
      normalized += '/api';
    }

    const testUrl = `${normalized}/config`;
    const response = await axios.get(testUrl, { timeout: 8000 });
    return {
      success: true,
      data: response.data?.data || response.data,
      normalizedApiUrl: normalized
    };
  } catch (err) {
    if (err.response && (err.response.status === 401 || err.response.status === 403 || err.response.status === 200)) {
      let normalized = apiUrl.trim().replace(/\/+$/, '');
      if (!normalized.endsWith('/api')) {
        normalized += '/api';
      }
      return {
        success: true,
        data: err.response.data?.data || err.response.data || {},
        normalizedApiUrl: normalized
      };
    }
    return {
      success: false,
      error: err.response?.data?.message || err.message || 'Gagal terhubung ke server sekolah.'
    };
  }
}

/**
 * Helper untuk membuat URL API dari slug sekolah (contoh: 'smpit-annajah').
 * Format standar: https://siakad.<slug>.garapedu.id/api
 * @param {string} input - Slug atau domain sekolah
 * @returns {string}
 */
export function resolveApiUrlFromInput(input) {
  const trimmed = (input || '').trim().toLowerCase();
  if (!trimmed) return '';

  // Jika input sudah berformat URL lengkap (http:// atau https://)
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  // Jika input berupa subdomain utuh (contoh: siakad.smpit.garapedu.id)
  if (trimmed.includes('.')) {
    return `https://${trimmed}/api`;
  }

  // Jika hanya slug (contoh: 'smpit-annajah')
  return `https://siakad.${trimmed}.garapedu.id/api`;
}
